import * as Sentry from '@sentry/react';
import { ExternalModuleSource } from 'shared/models/mfe';
import { buildSentryDsn } from 'shared/utils/sentry';
import * as ClientToggles from 'config/toggles';
import { TOGGLE_SENTRY } from 'config/toggles';
import DevsiteEnv from 'modules/core/DevsiteEnv';
import { browserHistory } from 'modules/core/history';

export const initializeSentry = () => {
  // Helper function to build DSN from module configuration
  const buildModuleDsn = (moduleKey: ExternalModuleSource) => {
    const moduleSentryConfig = DevsiteEnv.SENTRY?.DSN?.[moduleKey];
    if (moduleSentryConfig?.PROJECT_ID && moduleSentryConfig?.CLIENT_KEY) {
      return buildSentryDsn(
        DevsiteEnv.SENTRY?.CLIENT_FQDN ?? '',
        moduleSentryConfig.PROJECT_ID,
        moduleSentryConfig.CLIENT_KEY
      );
    }
    return null;
  };

  // Helper function to create route configuration from module and version
  const createRouteConfig = (dsn: string, version?: string) => ({
    dsn,
    release: version?.toString(),
  });

  // Helper function to handle module-based routing
  const getModuleRouting = <T extends Sentry.Event = Sentry.Event>(event: T) => {
    if (!event?.tags?.module) return null;

    const moduleKey = String(event.tags.module).toUpperCase() as ExternalModuleSource;
    const dsn = buildModuleDsn(moduleKey);

    return dsn ? [createRouteConfig(dsn, String(event.tags?.moduleVersion))] : null;
  };

  // Helper function to handle stacktrace-based routing
  const getStacktraceRouting = <T extends Sentry.Event = Sentry.Event>(event: T) => {
    const frames = event?.exception?.values?.[0]?.stacktrace?.frames;
    if (!frames) return null;

    // Find frames with module metadata containing a DSN
    const routeTo = frames
      .filter((frame) => frame.module_metadata?.dsn)
      .map((frame) => frame.module_metadata)
      .slice(-1); // using top frame only

    return routeTo.length ? routeTo : null;
  };

  // Transport function for multiplexed routing
  const transport = Sentry.makeMultiplexedTransport(Sentry.makeFetchTransport, (args) => {
    const event = args.getEvent([
      'event',
      'transaction',
      'replay_event',
      'span',
      'transaction',
      'session',
      'replay_recording',
      'attachment',
    ]);

    if (!event) {
      return [];
    }

    // Check for module-based routing (for spans that bypass beforeSend hooks)
    const moduleRouting = getModuleRouting(event);
    if (moduleRouting) {
      return moduleRouting;
    }

    // Check for explicit routing in event.extra
    if (event.extra?.ROUTE_TO && Array.isArray(event.extra.ROUTE_TO)) {
      return event.extra.ROUTE_TO;
    }

    return [];
  });

  // Event routing function for beforeSend and beforeSendTransaction
  const applyEventRouting = <T extends Sentry.Event = Sentry.Event>(event: T) => {
    // Handle module-based routing
    const moduleRouting = getModuleRouting(event);
    if (moduleRouting) {
      event.extra = {
        ...event.extra,
        ROUTE_TO: moduleRouting,
      };
    }

    // Handle stacktrace-based routing (takes precedence)
    const stacktraceRouting = getStacktraceRouting(event);
    if (stacktraceRouting) {
      event.extra = {
        ...event.extra,
        ROUTE_TO: stacktraceRouting,
      };
    }

    return event;
  };

  const { PROJECT_ID, CLIENT_KEY } = DevsiteEnv.SENTRY?.DSN?.DEVELOPER_SITE ?? {};

  Sentry.init({
    dsn: buildSentryDsn(DevsiteEnv.SENTRY?.CLIENT_FQDN, PROJECT_ID, CLIENT_KEY),
    enabled: TOGGLE_SENTRY,
    release: DevsiteEnv.VERSION,
    transport,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ['log', 'debug', 'error'], // matching the levels in wconsole.ts
      }),
      Sentry.moduleMetadataIntegration(),
      Sentry.reactRouterV5BrowserTracingIntegration({
        history: browserHistory,
      }),
      Sentry.replayIntegration({
        networkDetailAllowUrls: [window.location.origin],
        networkRequestHeaders: ['Cache-Control'],
        networkResponseHeaders: ['Referrer-Policy'],
        maskAllText: true,
        maskAllInputs: true,
        workerUrl: '/static/js/sentry-worker.js',
      }),
      Sentry.featureFlagsIntegration(),
    ],
    environment: (DevsiteEnv.FED_MODE ? `fed-` : '') + DevsiteEnv.ENV_LEVEL,
    tracesSampleRate: Number(DevsiteEnv.SENTRY?.TRACES?.SAMPLE_RATE),
    replaysSessionSampleRate: Number(DevsiteEnv.SENTRY?.REPLAY?.SESSION_SAMPLE_RATE),
    replaysOnErrorSampleRate: Number(DevsiteEnv.SENTRY?.REPLAY?.ERROR_SAMPLE_RATE),
    tracePropagationTargets: DevsiteEnv.SENTRY?.TRACES?.TARGETS,
    beforeSend: applyEventRouting<Sentry.ErrorEvent>,
    beforeSendTransaction: applyEventRouting,
  });

  // Set up feature flags
  const flagsIntegration = Sentry.getClient()?.getIntegrationByName<Sentry.FeatureFlagsIntegration>('FeatureFlags');
  if (flagsIntegration) {
    Object.entries(ClientToggles).forEach(([key, value]) => {
      flagsIntegration.addFeatureFlag(key.replace(/^TOGGLE_/, ''), value);
    });
  }
};
